import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { CheckCircle2, Clock, Flame, Loader2, MapPin, ShieldCheck, Undo2 } from 'lucide-react';
import { Card, Button } from '../components';
import {
  activeerWaakvlam,
  duurTekst,
  euro,
  fetchWerkgeverOverzicht,
  maakMandaat,
  trekMandaatIn,
  zegWaakvlamOp,
  type WerkgeverOverzicht,
} from '../services/vertrouwenspersoonApi';

const QUERY_KEY = ['vertrouwenspersoon', 'werkgever'];

const foutTekst = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

const ABONNEMENT_STATUS: Record<string, { tekst: string; kleur: string }> = {
  PENDING_MANDATE: { tekst: 'Wacht op SEPA-machtiging', kleur: 'bg-amber-100 text-amber-800' },
  ACTIVE: { tekst: 'Waakvlam brandt', kleur: 'bg-emerald-100 text-emerald-800' },
  PAST_DUE: { tekst: 'Incasso mislukt', kleur: 'bg-red-100 text-red-800' },
  CANCELED: { tekst: 'Opgezegd', kleur: 'bg-gray-100 text-gray-700' },
};

function maandLabel(periode: string) {
  const [j, m] = periode.split('-').map(Number);
  return new Date(j, m - 1, 1).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}

/**
 * Mijn Cockpit → tab Vertrouwenspersoon.
 * Deel 1: Waakvlam — vaste paraatheidsfee met 12-uurs reactiegarantie.
 * Deel 2: Zichtbaarheidsmandaten — maandbudget voor fysieke aanwezigheid op een vestiging (GPS-Grendel).
 */
export const EmployerVertrouwenspersoonModule: React.FC = () => {
  const { data, isLoading, error } = useQuery({ queryKey: QUERY_KEY, queryFn: fetchWerkgeverOverzicht, refetchInterval: 60_000 });
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden…
      </div>
    );
  }
  if (error || !data) return <Card className="text-red-700">Deze module kon niet worden geladen.</Card>;

  return (
    <div className="space-y-8">
      {searchParams.get('waakvlam') === 'gelukt' && (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>SEPA-machtiging afgegeven. Zodra Stripe dit bevestigt, brandt de Waakvlam en staat uw vertrouwenspersoon paraat.</div>
        </div>
      )}
      <WaakvlamDeel data={data.waakvlam} />
      <ZichtbaarheidDeel data={data.zichtbaarheid} />
    </div>
  );
};

// ─── Deel 1: Waakvlam ─────────────────────────────────────────────────────────

const WaakvlamDeel: React.FC<{ data: WerkgeverOverzicht['waakvlam'] }> = ({ data }) => {
  const queryClient = useQueryClient();
  const [vpId, setVpId] = useState('');
  const [partner, setPartner] = useState('');
  const [akkoord, setAkkoord] = useState(false);

  const geschikt = data.kandidaten.filter((k) => k.sla_akkoord);
  useEffect(() => {
    if (!vpId && geschikt.length === 1) setVpId(geschikt[0].professional_id);
  }, [geschikt, vpId]);

  const activeer = useMutation({
    mutationFn: () => activeerWaakvlam({ professional_id: vpId, salarisverwerker: partner || null, voorwaarden_hash: data.voorwaarden!.hash }),
    onSuccess: (r) => {
      if (r.checkoutUrl) {
        window.location.href = r.checkoutUrl;
        return;
      }
      toast.success('Waakvlam actief op uw bestaande SEPA-machtiging');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => toast.error(foutTekst(e, 'Activeren mislukt')),
  });
  const opzeggen = useMutation({
    mutationFn: zegWaakvlamOp,
    onSuccess: () => {
      toast.success('Waakvlam opgezegd per einde van deze maand');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => toast.error(foutTekst(e, 'Opzeggen mislukt')),
  });

  const sub = data.abonnement;
  const lopend = sub && sub.status !== 'PENDING_MANDATE';

  return (
    <section className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center flex-shrink-0">
          <Flame className="w-5 h-5 text-navy" />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-navy">Waakvlam — paraatheid van uw vertrouwenspersoon</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Voor een vaste maandelijkse paraatheidsfee staat een gecertificeerde vertrouwenspersoon klaar voor acute meldingen van
            integriteitsschendingen, intimidatie of zware frictie. Medewerkers melden zich anoniem via de chat in het medewerkersportaal.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Uitleg icon={Clock} titel={`Reactie binnen ${data.sla_uren} uur`}>
          Elke hulpoproep start automatisch een anonieme timer. De vertrouwenspersoon reageert binnen {data.sla_uren} uur via de chat van het platform —
          ook bij een melding midden in de nacht.
        </Uitleg>
        <Uitleg icon={Undo2} titel="Geld-terug-garantie">
          Wordt de termijn overschreden, dan springt de status op SLA_BREACH en storten wij de fee van die maand volledig terug op uw rekening.
        </Uitleg>
        <Uitleg icon={ShieldCheck} titel="U ziet niets van de melding">
          Inhoud, tijdstip en melder blijven geheim. U ziet per maand alleen of de reactietermijn is gehaald.
        </Uitleg>
      </div>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Maandelijkse tier</h3>
        <div className="grid gap-2 sm:grid-cols-3">
          {data.tiers.map((t) => {
            const actief = (sub?.tier ?? data.tier?.tier) === t.tier;
            return (
              <div key={t.tier} className={`rounded-lg border p-3 ${actief ? 'border-gold bg-gold/10' : 'border-gray-200'}`}>
                <p className="text-xs font-medium text-gray-500">{t.naam}</p>
                <p className="text-xl font-semibold text-navy mt-1">
                  {euro(t.maandfee)} <span className="text-xs font-normal text-gray-500">/ maand excl. btw</span>
                </p>
                {actief && <p className="text-xs font-medium text-navy mt-1">Uw organisatie</p>}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          {data.bedrijfsgrootte
            ? `De tier volgt automatisch uit ${data.bedrijfsgrootte.aantal.toLocaleString('nl-NL')} medewerkers (${
                data.bedrijfsgrootte.bron === 'PAYROLL' ? 'uw laatste salarisimport' : 'uw organisatieprofiel'
              }).`
            : 'Lees eerst uw medewerkersbestand in (tab L&O-flexbudget) of vul het aantal medewerkers in uw organisatieprofiel in.'}{' '}
          Stripe incasseert de fee via SEPA op de 1e van iedere maand; de eerste maand naar rato. {data.split.vp_pct}% gaat naar de
          vertrouwenspersoon als vergoeding voor de paraatheid, {data.split.platform_pct}% is platformfee.
        </p>
      </Card>

      {lopend && sub ? (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${ABONNEMENT_STATUS[sub.status]?.kleur}`}>
                {ABONNEMENT_STATUS[sub.status]?.tekst ?? sub.status}
              </span>
              <p className="text-sm text-gray-900 mt-2">
                Vertrouwenspersoon: <strong>{sub.vertrouwenspersoon.naam}</strong>
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Tier {sub.tier} · {euro(sub.maandfee)} per maand
                {sub.actief_sinds && ` · actief sinds ${new Date(sub.actief_sinds).toLocaleDateString('nl-NL')}`}
                {sub.opgezegd_op && ' · opgezegd per einde van de maand'}
              </p>
            </div>
            {!sub.opgezegd_op && (
              <Button
                variant="outline"
                size="sm"
                loading={opzeggen.isPending}
                onClick={() => window.confirm('Waakvlam opzeggen per einde van deze maand?') && opzeggen.mutate()}
              >
                Opzeggen
              </Button>
            )}
          </div>
          {data.perioden.length > 0 && (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="py-2 font-medium">Maand</th>
                  <th className="py-2 font-medium">Fee</th>
                  <th className="py-2 font-medium">Reactietermijn</th>
                </tr>
              </thead>
              <tbody>
                {data.perioden.map((p) => (
                  <tr key={p.periode} className="border-b border-gray-50">
                    <td className="py-2 capitalize">{maandLabel(p.periode)}</td>
                    <td className="py-2">{euro(p.bruto)}</td>
                    <td className="py-2">
                      {p.sla === 'GEHAALD' ? (
                        <span className="text-emerald-700">Gehaald</span>
                      ) : (
                        <span className="text-red-700">
                          Overschreden — {p.teruggestort ? 'fee teruggestort' : 'terugstorting in behandeling'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
        <Card>
          <h3 className="text-sm font-semibold text-gray-900">Waakvlam activeren</h3>
          {data.kandidaten.length === 0 ? (
            <p className="text-sm text-gray-600 mt-2">
              Er is nog geen vertrouwenspersoon met een actieve samenwerking in uw pool. Voeg de discipline toe in de Pool-Configurator.
            </p>
          ) : (
            <div className="mt-3 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Gekoppelde vertrouwenspersoon</span>
                <select value={vpId} onChange={(e) => setVpId(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Kies…</option>
                  {data.kandidaten.map((k) => (
                    <option key={k.professional_id} value={k.professional_id} disabled={!k.sla_akkoord}>
                      {k.naam}
                      {!k.sla_akkoord ? ' — heeft de 12-uurs voorwaarden nog niet geaccepteerd' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Via welke salarisverwerker kent u BeLagom? (optioneel)</span>
                <select value={partner} onChange={(e) => setPartner(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Geen / anders</option>
                  {data.salarisverwerkers.map((s) => (
                    <option key={s} value={s}>
                      {s === 'NMBRS' ? 'Nmbrs' : s === 'VISMA' ? 'Visma' : s}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">BeLagom draagt een deel van haar eigen platformfee af aan deze partner; uw fee verandert niet.</span>
              </label>
              {data.voorwaarden && (
                <>
                  <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700 font-sans">
                    {data.voorwaarden.tekst}
                  </pre>
                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input type="checkbox" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} className="mt-1" />
                    <span>Ik ga akkoord met deze voorwaarden (versie {data.voorwaarden.versie}) en machtig BeLagom tot maandelijkse SEPA-incasso.</span>
                  </label>
                </>
              )}
              <Button disabled={!vpId || !akkoord || !data.voorwaarden} loading={activeer.isPending} onClick={() => activeer.mutate()}>
                <Flame className="w-4 h-4 mr-1.5" /> Activeer Waakvlam
              </Button>
            </div>
          )}
        </Card>
      )}
    </section>
  );
};

// ─── Deel 2: Zichtbaarheidsmandaten ──────────────────────────────────────────

const ZichtbaarheidDeel: React.FC<{ data: WerkgeverOverzicht['zichtbaarheid'] }> = ({ data }) => {
  const queryClient = useQueryClient();
  const [locatie, setLocatie] = useState('');
  const [vp, setVp] = useState('');
  const [uren, setUren] = useState('4');
  const [tarief, setTarief] = useState('');

  useEffect(() => {
    if (!locatie && data.vestigingen.length === 1) setLocatie(data.vestigingen[0].id);
    if (!vp && data.vertrouwenspersonen.length === 1) setVp(data.vertrouwenspersonen[0].professional_id);
  }, [data, locatie, vp]);
  useEffect(() => {
    const gekozen = data.vertrouwenspersonen.find((v) => v.professional_id === vp);
    if (gekozen?.uurtarief) setTarief(String(gekozen.uurtarief));
  }, [vp, data.vertrouwenspersonen]);

  const minuten = Math.round(parseFloat(uren.replace(',', '.')) * 60);
  const uurtarief = parseFloat(tarief.replace(',', '.'));
  const plafondEuro = useMemo(() => (minuten > 0 && uurtarief > 0 ? (minuten / 60) * uurtarief : 0), [minuten, uurtarief]);

  const maak = useMutation({
    mutationFn: () => maakMandaat({ location_id: locatie, professional_id: vp, max_minuten_per_maand: minuten, uurtarief }),
    onSuccess: (r) => {
      toast.success(`Mandaat actief — referentiepunt: ${r.bag_adres}`, { duration: 7000 });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => toast.error(foutTekst(e, 'Mandaat aanmaken mislukt'), { duration: 8000 }),
  });
  const intrekken = useMutation({
    mutationFn: trekMandaatIn,
    onSuccess: () => {
      toast.success('Mandaat ingetrokken');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => toast.error(foutTekst(e, 'Intrekken mislukt')),
  });

  return (
    <section className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 text-navy" />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-navy">Zichtbaarheidsuren op locatie</h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Laat uw vertrouwenspersoon fysiek zichtbaar zijn op de werkvloer — een koffieronde of inloopspreekuur. U legt vooraf per vestiging
            een maandplafond en uurtarief vast; dat is een kredietplafond, er wordt niets vooraf geïncasseerd.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Uitleg icon={MapPin} titel="GPS-Grendel">
          De vertrouwenspersoon kan alleen inchecken binnen 100 meter van het BAG-adres van de vestiging. Daarbuiten blokkeert de knop.
        </Uitleg>
        <Uitleg icon={Clock} titel="Tijd op de seconde">
          De tijd tussen check-in en check-out wordt exact vastgelegd en nooit boven uw maandplafond gefactureerd.
        </Uitleg>
        <Uitleg icon={ShieldCheck} titel="Op uw verzamelfactuur">
          Onder "Fysieke Zichtbaarheidsuren (Geverifieerd op locatie)", tegen het vastgelegde tarief plus de gebruikelijke platformopslag en btw.
        </Uitleg>
      </div>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Nieuw mandaat</h3>
        {data.vestigingen.length === 0 || data.vertrouwenspersonen.length === 0 ? (
          <p className="text-sm text-gray-600">
            {data.vestigingen.length === 0
              ? 'Leg eerst een vestiging met straat, huisnummer en postcode vast in uw organisatieprofiel.'
              : 'Er is nog geen vertrouwenspersoon met een actieve samenwerking in uw pool.'}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Vestiging</span>
              <select value={locatie} onChange={(e) => setLocatie(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="">Kies…</option>
                {data.vestigingen.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.naam} — {[l.adres, l.postcode, l.plaats].filter(Boolean).join(', ')}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Vertrouwenspersoon</span>
              <select value={vp} onChange={(e) => setVp(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="">Kies…</option>
                {data.vertrouwenspersonen.map((v) => (
                  <option key={v.professional_id} value={v.professional_id}>
                    {v.naam}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Maximaal aantal uren per maand</span>
              <input type="number" min={0.25} max={160} step={0.25} value={uren} onChange={(e) => setUren(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Uurtarief (excl. platformopslag en btw)</span>
              <input type="number" min={1} step={0.01} value={tarief} onChange={(e) => setTarief(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {plafondEuro > 0 ? `Kredietplafond: maximaal ${euro(plafondEuro)} per maand excl. platformopslag en btw.` : ' '}
              </p>
              <Button disabled={!locatie || !vp || !(minuten >= 15) || !(uurtarief > 0)} loading={maak.isPending} onClick={() => maak.mutate()}>
                Mandaat vastleggen
              </Button>
            </div>
          </div>
        )}
      </Card>

      {data.mandaten.length > 0 && (
        <div className="space-y-2">
          {data.mandaten.map((m) => {
            const plafondMs = m.max_minuten_per_maand * 60_000;
            const pct = Math.min(100, Math.round((m.gebruikt_ms / plafondMs) * 100));
            return (
              <Card key={m.id} className={m.status === 'INGETROKKEN' ? 'opacity-60' : ''}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {m.vestiging.naam} · {m.vertrouwenspersoon.naam}
                    </p>
                    <p className="text-xs text-gray-500">
                      Referentiepunt: {m.vestiging.bag_adres ?? [m.vestiging.adres, m.vestiging.postcode, m.vestiging.plaats].filter(Boolean).join(', ')} ·{' '}
                      {euro(m.uurtarief)}/u
                    </p>
                    {m.lopende_ronde_sinds && (
                      <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                        <MapPin className="w-3 h-3" /> Nu aanwezig op locatie sinds{' '}
                        {new Date(m.lopende_ronde_sinds).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  {m.status === 'ACTIEF' ? (
                    <Button variant="outline" size="sm" loading={intrekken.isPending && intrekken.variables === m.id}
                      onClick={() => window.confirm('Mandaat intrekken? Een lopende ronde wordt afgesloten.') && intrekken.mutate(m.id)}>
                      Intrekken
                    </Button>
                  ) : (
                    <span className="text-xs text-gray-500">Ingetrokken</span>
                  )}
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span className="capitalize">{maandLabel(m.periode)}</span>
                    <span>
                      {duurTekst(m.gebruikt_ms)} van {duurTekst(plafondMs)}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full ${pct >= 100 ? 'bg-red-500' : 'bg-navy'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

const Uitleg: React.FC<{ icon: React.ComponentType<{ className?: string }>; titel: string; children: React.ReactNode }> = ({ icon: Icon, titel, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <p className="flex items-center gap-1.5 text-sm font-semibold text-navy">
      <Icon className="w-4 h-4 text-gold" /> {titel}
    </p>
    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{children}</p>
  </div>
);

export default EmployerVertrouwenspersoonModule;
